import React, { useState } from "react";
import { useAuth } from "../components/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import LegalModals, { legalLinkStyle } from "../components/LegalModals";

export default function LoginPage() {
    const { login, register, setToken } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectTo = searchParams.get("redirect");
    const [mode, setMode] = useState("login"); // login | register | forgot | passcode | api
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passcode, setPasscode] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            if (mode === "passcode") {
                const { API_BASE } = await import("../apiBase");
                const axios = (await import("axios")).default;
                const { data } = await axios.post(`${API_BASE}/api/auth/redeem-passcode`, { code: passcode, password });
                // Auto-login with returned token
                if (data.access_token) {
                    localStorage.setItem("token", data.access_token);
                    window.location.href = "/positioning";
                }
            } else if (mode === "forgot") {
                const { API_BASE } = await import("../apiBase");
                const axios = (await import("axios")).default;
                await axios.post(`${API_BASE}/api/auth/forgot-password`, { email, origin_url: window.location.origin });
                setForgotSent(true);
            } else if (mode === "register") {
                await register(name, email, password);
                navigate(redirectTo || "/subscribe");
            } else {
                await login(email, password);
                navigate(redirectTo || "/positioning");
            }
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (typeof detail === "string") {
                setError(detail);
            } else if (Array.isArray(detail)) {
                setError(detail.map(d => d.msg || d.message || JSON.stringify(d)).join("; "));
            } else if (err.message) {
                setError(`Network error: ${err.message}`);
            } else {
                setError("Something went wrong. Please try again.");
            }
        } finally {
            setBusy(false);
        }
    };

    // Full-screen modes: register, passcode, forgot, api
    const isFullScreen = mode === "register" || mode === "passcode" || mode === "forgot" || mode === "api";

    if (isFullScreen) {
        return (
            <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--pord-bg, #1a1e14)", fontFamily: "'DM Sans', sans-serif" }}>
                <div style={{ width: 400, padding: "40px 36px", borderRadius: 12, background: "rgba(184,212,208,0.03)", border: "1px solid rgba(184,212,208,0.08)" }}>
                    <h1 style={{ fontSize: 36, fontWeight: 300, color: "#DAF2D0", marginBottom: 6, letterSpacing: 2 }}>Pord.</h1>
                    <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 32 }}>
                        {mode === "forgot" ? "Reset your password" : mode === "passcode" ? "Redeem your guest pass" : mode === "api" ? "" : "Create your account"}
                    </p>

                    {mode === "api" ? (
                        <div style={{ textAlign: "left" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                                <span style={{ fontSize: 16, color: "var(--pord-teal, #9aada8)", fontWeight: 500 }}>Data API Access</span>
                                <span style={{ fontSize: 11, color: "var(--pord-teal, #9aada8)", background: "rgba(154,173,168,0.12)", padding: "3px 10px", borderRadius: 99, opacity: 0.7 }}>for developers</span>
                            </div>
                            <p style={{ fontSize: 15, color: "var(--pord-teal, #9aada8)", marginBottom: 18, lineHeight: 1.6 }}>
                                API Connect to pull curated positioning data + original CoT reports directly into your systems.
                            </p>
                            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.5)", lineHeight: 1.8, marginBottom: 20 }}>
                                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
                                    <span style={{ color: "var(--pord-teal, #9aada8)", fontWeight: 500 }}>Annual subscription</span>
                                    <span style={{ color: "#DAF2D0", fontSize: 28, fontWeight: 300 }}>$100</span>
                                    <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>/year</span>
                                </div>
                                <div>
                                    <span style={{ color: "var(--pord-teal, #9aada8)", fontWeight: 500 }}>Historical curated positioning data</span>{" "}
                                    <span style={{ color: "rgba(255,255,255,0.45)" }}>– 10 years, and original data from CoT reports : all available fields included –</span>{" "}
                                    <span style={{ color: "rgba(255,255,255,0.45)" }}>one-time fee of</span>{" "}
                                    <span style={{ color: "#DAF2D0", fontSize: 28, fontWeight: 300 }}>$50</span>
                                </div>
                            </div>
                            <button onClick={() => { setMode("register"); setError(""); }}
                                className="cta-pill cta-pill-filled" style={{ width: "100%", padding: "12px 0", borderRadius: 8, fontSize: 14, fontWeight: 500, justifyContent: "center", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", marginBottom: 16 }}>
                                Create Account to Subscribe
                            </button>
                            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", textAlign: "center" }}>Secure payment via Stripe</p>
                        </div>
                    ) : mode === "forgot" && forgotSent ? (
                        <div>
                            <p style={{ fontSize: 14, color: "#DAF2D0", marginBottom: 16 }}>If that email exists, a reset link has been sent. Check your inbox.</p>
                            <button onClick={() => { setMode("login"); setForgotSent(false); setError(""); }} style={linkBtn}>Back to sign in</button>
                        </div>
                    ) : mode === "passcode" ? (
                        <form onSubmit={handleSubmit}>
                            <input type="text" placeholder="Passcode" value={passcode} onChange={(e) => setPasscode(e.target.value.toUpperCase())}
                                required style={{ ...inputStyle, textTransform: "uppercase", letterSpacing: 2, fontWeight: 600, textAlign: "center" }} />
                            <input type="password" placeholder="Create Password" value={password} onChange={(e) => setPassword(e.target.value)} required style={inputStyle} />
                            {error && <div style={{ color: "#e57373", fontSize: 12, marginBottom: 12 }}>{error}</div>}
                            <button type="submit" disabled={busy} className="cta-pill cta-pill-filled" style={{
                                width: "100%", padding: "12px 0", borderRadius: 8, fontSize: 14, fontWeight: 500,
                                justifyContent: "center", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1,
                                fontFamily: "'DM Sans', sans-serif", marginBottom: 16,
                            }}>
                                {busy ? "..." : "Redeem Passcode"}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            {mode === "register" && (
                                <input data-testid="register-name" type="text" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required style={inputStyle} />
                            )}
                            <input data-testid="login-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />
                            {mode !== "forgot" && (
                                <input data-testid="login-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required style={inputStyle} />
                            )}
                            {error && <div data-testid="auth-error" style={{ color: "#e57373", fontSize: 12, marginBottom: 12 }}>{error}</div>}
                            <button data-testid="auth-submit" type="submit" disabled={busy} className="cta-pill cta-pill-filled" style={{
                                width: "100%", padding: "12px 0", borderRadius: 8, fontSize: 14, fontWeight: 500, justifyContent: "center",
                                cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1, fontFamily: "'DM Sans', sans-serif", marginBottom: 16,
                            }}>
                                {busy ? "..." : mode === "forgot" ? "Send Reset Link" : "Create Account"}
                            </button>
                        </form>
                    )}

                    <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
                        <button onClick={() => { setMode("login"); setError(""); setForgotSent(false); }} style={linkBtn}>Back to sign in</button>
                    </div>
                </div>
            </div>
        );
    }

    // Two-column login layout
    return (
        <div style={{
            minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
            background: "var(--pord-bg, #1a1e14)", fontFamily: "'DM Sans', sans-serif",
        }}>
            <div style={{ display: "flex", gap: 24, alignItems: "stretch" }}>
                {/* LEFT: Sign In */}
                <div style={{
                    width: 380, padding: "40px 36px", borderRadius: 12,
                    background: "rgba(184,212,208,0.03)", border: "1px solid rgba(184,212,208,0.08)",
                    display: "flex", flexDirection: "column",
                }}>
                    <h1 data-testid="login-title" style={{ fontSize: 36, fontWeight: 300, color: "#DAF2D0", marginBottom: 6, letterSpacing: 2 }}>
                        Pord.
                    </h1>
                    <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 32 }}>
                        Sign in to your account
                    </p>

                    <form onSubmit={handleSubmit}>
                        <input data-testid="login-email" type="text" placeholder="Email or Passcode" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />
                        <input data-testid="login-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required style={inputStyle} />
                        {error && <div data-testid="auth-error" style={{ color: "#e57373", fontSize: 12, marginBottom: 12 }}>{error}</div>}
                        <button data-testid="auth-submit" type="submit" disabled={busy} className="cta-pill cta-pill-filled" style={{
                            width: "100%", padding: "12px 0", borderRadius: 8, fontSize: 14, fontWeight: 500, justifyContent: "center",
                            cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1, fontFamily: "'DM Sans', sans-serif", marginBottom: 16,
                        }}>
                            {busy ? "..." : "SIGN IN"}
                        </button>
                    </form>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            <button onClick={() => { setMode("forgot"); setError(""); }} style={linkBtn}>Forgot password?</button>
                            <button data-testid="switch-to-passcode" onClick={() => { setMode("passcode"); setError(""); }} style={linkBtn}>Have a passcode?</button>
                        </div>
                        <button onClick={() => navigate("/set-password")} style={linkBtn}>Create password</button>
                    </div>
                </div>

                {/* RIGHT: CTA panel */}
                <div style={{
                    width: 380, padding: "40px 36px", borderRadius: 12,
                    background: "rgba(184,212,208,0.03)", border: "1px solid rgba(184,212,208,0.08)",
                    display: "flex", flexDirection: "column", justifyContent: "center", gap: 20,
                }}>
                    {/* Data API — filled button, same style as SIGN IN */}
                    <button data-testid="data-api-btn" onClick={() => navigate("/subscribe")}
                        className="cta-pill cta-pill-filled" style={{
                            width: "100%", padding: "28px 28px", borderRadius: 10,
                            fontSize: 14, fontWeight: 400, fontFamily: "'DM Sans', sans-serif",
                            cursor: "pointer", textAlign: "left", lineHeight: 1.6,
                            display: "flex", flexDirection: "column", gap: 8,
                            textTransform: "none",
                        }}>
                        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", opacity: 0.7 }}>Data API</span>
                        <span style={{ fontSize: 16, fontWeight: 300 }}>
                            API Connect integrating positioning data, historical series, and original CFTC CoT report records directly into your models, dashboards, and internal research workflows.
                        </span>
                        <span style={{ fontSize: 12, textDecoration: "underline", textUnderlineOffset: 3, opacity: 0.7 }}>Learn more</span>
                    </button>

                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <a href="https://pordapp.substack.com/p/reading-the-futures-a-positioning" target="_blank" rel="noopener noreferrer"
                            data-testid="reading-futures-link"
                            style={{
                                fontSize: 13, color: "var(--pord-teal, #9aada8)", textDecoration: "none",
                                padding: "10px 20px", borderRadius: 999, textAlign: "center", boxSizing: "border-box",
                                background: "rgba(154,173,168,0.06)", border: "1px solid rgba(154,173,168,0.10)",
                                fontFamily: "'DM Sans', sans-serif", transition: "all 200ms",
                            }}>
                            Reading the Futures: A Positioning Read Tool
                        </a>
                        <a href="/preview" style={{ fontSize: 13, color: "var(--pord-teal, #9aada8)", textDecoration: "none", textAlign: "center" }}>
                            View preview &#8594;
                        </a>
                    </div>
                </div>
            </div>

            {/* Legal footer */}
            <LegalModals>
                {({ setShowTerms, setShowCookie, setShowDNS }) => (
                    <div style={{
                        position: "fixed", bottom: 0, left: 0, right: 0,
                        padding: "14px 0", textAlign: "center",
                        background: "linear-gradient(transparent, var(--pord-bg, #1a1e14) 40%)",
                    }}>
                        <div style={{ display: "flex", justifyContent: "center", gap: 24, fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
                            <button onClick={() => setShowTerms(true)}
                                data-testid="terms-link"
                                style={legalLinkStyle}
                                onMouseEnter={e => e.target.style.color = "#DAF2D0"}
                                onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.35)"}>
                                Terms & Policy
                            </button>
                            <span style={{ opacity: 0.3 }}>&middot;</span>
                            <button onClick={() => setShowCookie(true)}
                                data-testid="cookie-link"
                                style={legalLinkStyle}
                                onMouseEnter={e => e.target.style.color = "#DAF2D0"}
                                onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.35)"}>
                                Cookie Policy
                            </button>
                            <span style={{ opacity: 0.3 }}>&middot;</span>
                            <button onClick={() => setShowDNS(true)}
                                data-testid="dns-link"
                                style={legalLinkStyle}
                                onMouseEnter={e => e.target.style.color = "#DAF2D0"}
                                onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.35)"}>
                                Do Not Sell My Info
                            </button>
                        </div>
                    </div>
                )}
            </LegalModals>
        </div>
    );
}

const inputStyle = {
    width: "100%", padding: "11px 14px", marginBottom: 14, borderRadius: 8,
    border: "1px solid rgba(184,212,208,0.12)", background: "rgba(30,34,24,0.8)",
    color: "#DAF2D0", fontSize: 13, outline: "none", fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box",
};

const linkBtn = {
    background: "none", border: "none", color: "#DAF2D0", cursor: "pointer",
    fontSize: 12, textDecoration: "underline", padding: 0, fontFamily: "'DM Sans', sans-serif",
};
